import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight,
  ChevronRight,
  Mountain,
  Target,
  Zap,
  Play
} from 'lucide-react';
import { ProductCard, CategoryCard, TechnologyCard, TechLabel } from '../components/common/TacticalCard';
import { productsApi, technologiesApi, categoriesApi, usecasesApi, showcaseApi } from '../lib/api';
import { fadeUpVariant, staggerContainer } from '../lib/animations';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/common/SEO';
import ImmersiveHero from '../components/showcase/ImmersiveHero';
import MissionSection from '../components/showcase/MissionSection';
import UseCasesShowcase from '../components/showcase/UseCasesShowcase';

// Hero Section with Parallax and CMS Content
const HeroSection = () => {
  const { getSetting } = useSiteSettings();
  const { language, t } = useLanguage();

  // Get CMS values with language support
  const heroTitle = language === 'en' 
    ? getSetting('hero_title_en', getSetting('hero_title', 'TACTICAL'))
    : getSetting('hero_title', 'TACTICAL');
  const heroSubtitle = language === 'en'
    ? getSetting('hero_subtitle_en', getSetting('hero_subtitle', 'OUTDOOR WEAR'))
    : getSetting('hero_subtitle', 'OUTDOOR WEAR');
  const heroDescription = language === 'en'
    ? getSetting('hero_description_en', getSetting('hero_description', 'Designed for tough conditions.'))
    : getSetting('hero_description', 'Zorlu koşullar için tasarlandı. Premium malzemeler, dayanıklı yapı, profesyonel performans. Her ortamda size eşlik eden taktik giyim.');
  const heroImage = getSetting('hero_image', 'https://images.unsplash.com/photo-1569242838421-c6f57b9a02df?w=1920&q=80');
  const heroVideo = getSetting('hero_video', '');
  const heroBannerLink = getSetting('hero_banner_link', '');
  const heroCtaText = language === 'en'
    ? getSetting('hero_cta_text_en', getSetting('hero_cta_text', 'EXPLORE STORE'))
    : getSetting('hero_cta_text', 'MAĞAZAYI KEŞFET');
  const heroCtaLink = getSetting('hero_cta_link', 'https://www.taktikstore.com');

  return (
    <ImmersiveHero
      title={heroTitle}
      subtitle={heroSubtitle}
      description={heroDescription}
      videoUrl={heroVideo}
      imageUrl={heroImage}
      ctaText={heroCtaText}
      ctaLink={heroCtaLink}
      bannerLink={heroBannerLink}
    />
  );
};

// Round Categories Section with Video Support
const CategoriesSection = () => {
  const [categories, setCategories] = useState([]);
  const { language, t, getLocalizedField } = useLanguage();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesApi.getAll();
        setCategories(response.data.categories);
      } catch (err) {
        // Mock categories
        setCategories([
          { category_id: '1', name: 'Pantolonlar', name_en: 'Pants', slug: 'pantolon', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=200&h=200&fit=crop', video_url: '' },
          { category_id: '2', name: 'Montlar', name_en: 'Jackets', slug: 'mont', image: 'https://images.unsplash.com/photo-1544923246-77307dd628b3?w=200&h=200&fit=crop', video_url: '' },
          { category_id: '3', name: 'Gömlekler', name_en: 'Shirts', slug: 'gomlek', image: 'https://images.unsplash.com/photo-1598033129183-c4f50c736f10?w=200&h=200&fit=crop', video_url: '' },
          { category_id: '4', name: 'Tişörtler', name_en: 'T-Shirts', slug: 'tshirt', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop', video_url: '' },
          { category_id: '5', name: 'Aksesuarlar', name_en: 'Accessories', slug: 'aksesuar', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200&h=200&fit=crop', video_url: '' },
          { category_id: '6', name: 'Ayakkabılar', name_en: 'Footwear', slug: 'ayakkabi', image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200&h=200&fit=crop', video_url: '' },
        ]);
      }
    };
    fetchCategories();
  }, []);

  return (
    <section className="py-16 bg-vav-bg relative overflow-hidden" data-testid="categories-section">
      {/* Background decoration */}
      <div className="absolute inset-0 animated-grid opacity-20" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <TechLabel variant="accent" className="mb-4">{t('[KATEGORİLER]', '[CATEGORIES]')}</TechLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-vav-fg">
            {t('KOLEKSİYONLAR', 'COLLECTIONS')}
          </h2>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-8 sm:gap-10 lg:gap-12">
          {categories.map((cat, index) => (
            <CategoryCard
              key={cat.category_id}
              name={getLocalizedField(cat, 'name')}
              slug={cat.slug}
              image={cat.image}
              video_url={cat.video_url}
              index={index}
              external_url={cat.external_url}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section with Image dissolve - Now using imported component
const MissionSectionWrapper = () => {
  const { getSetting } = useSiteSettings();
  const { language, t } = useLanguage();
  
  // Get CMS values with language support
  const missionTitle = language === 'en'
    ? getSetting('mission_title_en', getSetting('mission_title', 'FIELD READY'))
    : getSetting('mission_title', 'FIELD READY');
  const missionText = language === 'en'
    ? getSetting('mission_text_en', getSetting('mission_text', 'VAV Wear has been a pioneer in tactical and outdoor wear since 2020.'))
    : getSetting('mission_text', 'VAV Wear, 2020 yılından bu yana taktik ve outdoor giyim sektöründe öncü bir marka olarak faaliyet göstermektedir. Tasarımlarımız, profesyonellerin zorlu saha koşullarında ihtiyaç duyduğu dayanıklılık, konfor ve işlevselliği bir arada sunar.');
  const missionSecondaryText = language === 'en'
    ? getSetting('mission_secondary_text_en', getSetting('mission_secondary_text', 'Each product is made with military-grade tested materials.'))
    : getSetting('mission_secondary_text', 'Her ürün, askeri standartlarda test edilmiş malzemelerle üretilir ve modern yaşamın dinamik ihtiyaçlarına uygun olarak tasarlanır.');
  const missionImage = getSetting('mission_image', 'https://images.unsplash.com/photo-1683642765591-2370edc15193?w=800&q=80');
  const missionVideo = getSetting('mission_video', '');

  return (
    <MissionSection
      title={missionTitle}
      description={missionText}
      secondaryDescription={missionSecondaryText}
      imageUrl={missionImage}
      videoUrl={missionVideo}
      stats={[
        { value: '50+', label: t('Ürün', 'Products'), icon: 'Package' },
        { value: '10K+', label: t('Müşteri', 'Customers'), icon: 'Users' },
        { value: '4.9', label: t('Puan', 'Rating'), icon: 'Star' },
      ]}
    />
  );
};

// Tech Materials Section (from admin)
const TechMaterialsSection = () => {
  const [technologies, setTechnologies] = useState([]);

  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        const response = await technologiesApi.getAll();
        setTechnologies(response.data.technologies);
      } catch (err) {
        // Use mock data
        setTechnologies([
          { tech_id: '1', name: 'Su İtici', slug: 'su-itici', icon: 'Droplets', description: 'DWR kaplama teknolojisi', color: '#c4a84b' },
          { tech_id: '2', name: 'Nefes Alabilir', slug: 'nefes-alabilir', icon: 'Wind', description: 'Mikro delikli kumaş', color: '#39ff14' },
          { tech_id: '3', name: 'Ripstop', slug: 'ripstop', icon: 'Shield', description: 'Yırtılmaya karşı direnç', color: '#c4a84b' },
          { tech_id: '4', name: '4-Yön Streç', slug: '4-yon-strec', icon: 'Zap', description: 'Hareket özgürlüğü', color: '#ff4d00' },
        ]);
      }
    };
    fetchTechnologies();
  }, []);

  return (
    <section className="relative py-24 bg-vav-bg" data-testid="tech-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-tactical bg-grid opacity-5" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <TechLabel variant="accent" className="mb-4">[TEKNOLOJİ]</TechLabel>
          <h2 className="font-heading text-4xl sm:text-5xl text-vav-fg mb-4">
            TECH MATERIALS
          </h2>
          <p className="text-vav-fg-muted max-w-2xl mx-auto">
            Teknolojiye tıklayarak o özelliğe sahip ürünleri keşfedin.
          </p>
        </motion.div>

        {/* Technologies Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {technologies.map((tech, index) => (
            <TechnologyCard key={tech.tech_id} tech={tech} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Products Section
const FeaturedProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productsApi.getFeatured(8);
        setProducts(response.data.products);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <section className="relative py-24 bg-vav-bg-paper" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div>
            <TechLabel variant="gold" className="mb-4">[ÖNE ÇIKAN]</TechLabel>
            <h2 className="font-heading text-4xl sm:text-5xl text-vav-fg">
              ÜRÜN VİTRİNİ
            </h2>
          </div>
          <a 
            href="https://www.taktikstore.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 sm:mt-0 flex items-center gap-2 text-vav-accent-cyan hover:underline group"
          >
            Tüm Ürünler
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <ArrowRight className="w-4 h-4" />
            </motion.div>
          </a>
        </motion.div>

        {/* Products Grid - Asymmetric */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-[3/4] bg-vav-bg rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product, index) => (
              <ProductCard key={product.product_id} product={product} index={index} />
            ))}
          </div>
        )}

        {/* CTA */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <a 
            href="https://www.taktikstore.com"
            target="_blank"
            rel="noopener noreferrer"
            className="tactical-btn inline-flex items-center gap-2"
          >
            MAĞAZAYI GEZ
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Use Cases Section - Now using imported showcase component
const UseCasesSectionWrapper = () => {
  const [useCases, setUseCases] = useState([]);

  useEffect(() => {
    const fetchUseCases = async () => {
      try {
        const response = await usecasesApi.getAll();
        if (response.data.usecases && response.data.usecases.length > 0) {
          setUseCases(response.data.usecases);
        }
      } catch (err) {
        // Will use defaults from UseCasesShowcase component
        console.log('Using default use cases');
      }
    };
    fetchUseCases();
  }, []);

  return <UseCasesShowcase useCases={useCases} />;
};

// Newsletter Section
const NewsletterSection = () => {
  return (
    <section className="relative py-24 bg-vav-bg-paper" data-testid="newsletter-section">
      <div className="absolute inset-0 bg-grid-tactical bg-grid opacity-5" />
      
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <TechLabel variant="accent" className="mb-4">[BÜLTEN]</TechLabel>
          <h2 className="font-heading text-4xl sm:text-5xl text-vav-fg mb-4">
            SAHADA KAL
          </h2>
          <p className="text-vav-fg-muted mb-8">
            Yeni ürünler, özel indirimler ve taktik içeriklerden haberdar ol.
          </p>

          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="E-posta adresiniz"
              className="flex-1 px-4 py-3 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none rounded-lg"
            />
            <button type="submit" className="tactical-btn whitespace-nowrap">
              ABONE OL
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// Main Landing Page Component
const Landing = () => {
  return (
    <motion.main 
      className="min-h-screen" 
      data-testid="landing-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <SEO 
        title="Ana Sayfa"
        description="VAV Wear Tactical & Outdoor - Premium taktik ve outdoor giyim. Zorlu koşullar için tasarlandı. Askeri standartlarda kalite, profesyonel performans."
      />
      <HeroSection />
      <CategoriesSection />
      <MissionSectionWrapper />
      <TechMaterialsSection />
      <UseCasesSectionWrapper />
      <NewsletterSection />
    </motion.main>
  );
};

export default Landing;
